/**
 * Tipos e catálogos compartilhados entre server, web e simulator.
 */
/**
 * O critério ÚNICO de ausência, vencimento e impossibilidade de um dado.
 *
 * Reexportado daqui (e não apenas de `./frescor.js`) porque o pacote tem um
 * único `exports` no `package.json`: um módulo importável por dois caminhos
 * diferentes é a porta por onde nasce a segunda cópia do critério — que é
 * exatamente o mecanismo que este módulo existe para fechar.
 */
export * from './campos.js';
export * from './frescor.js';
/**
 * Os envelopes das rotas de frota.
 *
 * Publicados porque servidor e cliente foram escritos em paralelo contra um
 * contrato que ninguém havia publicado: o servidor emitia `snake_case`, o
 * cliente consumia `camelCase`, a fronteira era um `as` sobre `r.json()`, e o
 * `tsc` ficou limpo dos dois lados. O portal inteiro caiu no navegador — não a
 * tela de frota, o portal, incluindo Carregadores e Subestação. Ver `fleet.ts`.
 */
export * from './fleet.js';
/**
 * Os envelopes das rotas de CARREGADOR, e a whitelist de impossibilidade deles.
 *
 * Mesma razão de `fleet.js`, e aqui o custo já estava medido antes de nascer:
 * havia CINCO declarações locais divergentes da mesma linha de carregador, em
 * quatro arquivos do front (`ChargersPage`, `ChargerDetailPage`,
 * `GaragesTariffsPanel` e duas dentro de `modules-extra`). É o par
 * `VehicleSnapshot` × `VehicleRow` do ADR 0003 multiplicado por cinco.
 *
 * `campos-carregador.js` é uma união SEPARADA de `UnsupportedField`, e a razão é
 * mecânica: aquela é consumida por `Record` exaustivos nas seis telas de frota.
 * Ver o cabeçalho de `campos-carregador.ts`.
 */
export * from './campos-carregador.js';
export * from './carregadores.js';
export const NXVOLT_VERSION = '0.0.1';
/** Módulos temporariamente indisponíveis para toda a instalação. */
export const DISABLED_MODULE_SLUGS = [];
export function isModuleEnabled(slug) {
    return !DISABLED_MODULE_SLUGS.includes(slug);
}
/**
 * Recursos que seguem disponíveis para integrações, mas não são oferecidos na
 * interface enquanto sua fonte operacional ainda não estiver contratada.
 *
 * `saude_bateria` saiu daqui em 26/09/2026: a tela estava pronta e escondida.
 * Com o cadastro de veículos ligado ao VGPL ela passou a ter 16 ônibus com
 * histórico real, então não havia mais motivo para ficar invisível.
 */
export const HIDDEN_MODULE_SLUGS = [];
export function isModuleVisible(slug) {
    return !HIDDEN_MODULE_SLUGS.includes(slug);
}
/** Catálogo canônico. Deve casar com o seed em @nxvolt/db. */
/**
 * Nomes revisados em 26/09/2026. Os `slug` continuam intocados — são rota,
 * permissão e chave de seed; só o rótulo visível mudou.
 *
 * Critérios aplicados:
 *  - Nada de híbrido de idioma ("Painel Wall" dizia painel duas vezes).
 *  - Nada de abreviação ("Telemetria Sub." não revelava de quê).
 *  - Nada de parêntese explicando a técnica ("(Heatmap)" descrevia o
 *    desenho, não o conteúdo).
 *  - Nada de jargão de implementação ("Sessões Órfãs" é como o código
 *    chama a transação sem StopTransaction; o operador pensa "não fechou").
 *  - Sigla sem expansão vira nome por extenso ("CCO").
 *
 * Duas colisões apareceram ao renomear e foram resolvidas aqui:
 *  - `chargers` virou "Carregadores", então a categoria homônima passou a
 *    "Pátio" — o lugar físico onde eles ficam.
 *  - "Visão Geral" existia no Financeiro e viraria irmão de "Início";
 *    virou "Resumo Financeiro".
 */
export const MODULES_CATALOG = [
    // Início — as duas telas que abrem o dia.
    { slug: 'cco', name: 'Central de Operações', category: 'principal', icon: '🎯', order: 5 },
    { slug: 'dashboard', name: 'Visão Geral', category: 'principal', icon: '📊', order: 10 },
    // Pátio — o hardware e o que acontece com ele.
    { slug: 'chargers', name: 'Carregadores', category: 'carregadores', icon: '🔌', order: 110 },
    { slug: 'charging_wall', name: 'Mural de Pátio', category: 'carregadores', icon: '📺', order: 120 },
    { slug: 'maintenance', name: 'Manutenção', category: 'carregadores', icon: '🔧', order: 130 },
    { slug: 'charger_approval', name: 'Novos Carregadores', category: 'carregadores', icon: '✅', order: 140 },
    // Renomeado de `battery_health` -> `saude_bateria` na migration 0013 (ADR 0002).
    // Sai de 'carregadores' para 'frota': a tela mostra SOH por ONIBUS.
    // Estava entre equipamentos do patio, e e sobre veiculo. Order 425 a
    // coloca depois do cadastro de Veiculos e antes de Motoristas.
    // Operação — o turno acontecendo. Manobrista veio de "carregadores":
    // é uma fila de veículos conduzida por uma pessoa, não um equipamento.
    { slug: 'manobrista', name: 'Manobrista', category: 'operacao', icon: '🚦', order: 205 },
    { slug: 'live', name: 'Monitor ao Vivo', category: 'operacao', icon: '📡', order: 210 },
    { slug: 'alerts', name: 'Alertas', category: 'operacao', icon: '🔔', order: 220 },
    { slug: 'sessions', name: 'Recargas', category: 'operacao', icon: '⚡', order: 230 },
    { slug: 'charge_phases', name: 'Etapas da Recarga', category: 'operacao', icon: '📈', order: 240 },
    { slug: 'orphan_sessions', name: 'Recargas em Aberto', category: 'operacao', icon: '👻', order: 250 },
    { slug: 'heatmap_utilizacao', name: 'Ocupação', category: 'operacao', icon: '🌡️', order: 260 },
    { slug: 'ocpp_logs', name: 'Comunicação dos Carregadores', category: 'operacao', icon: '📜', order: 270 },
    // Energia — a conta de luz e a subestação.
    { slug: 'energia_demanda', name: 'Consumo e Demanda', category: 'gestao_eletrica', icon: '⚡', order: 305 },
    { slug: 'telemetria_sub', name: 'Subestação', category: 'gestao_eletrica', icon: '🗼', order: 310 },
    // Frota
    { slug: 'telemetria_frota', name: 'Dados dos Ônibus', category: 'frota', icon: '🛰️', order: 405 },
    { slug: 'mapa_frota', name: 'Mapa ao Vivo', category: 'frota', icon: '🗺️', order: 410 },
    { slug: 'fleet', name: 'Veículos', category: 'frota', icon: '🚚', order: 420 },
    { slug: 'saude_bateria', name: 'Saúde da Bateria', category: 'frota', icon: '❤️', order: 425 },
    { slug: 'drivers', name: 'Motoristas', category: 'frota', icon: '🪪', order: 430 },
    // Financeiro
    { slug: 'financial', name: 'Resumo', category: 'financeiro', icon: '💰', order: 510 },
    { slug: 'rel_consumo', name: 'Consumo por Motorista', category: 'financeiro', icon: '📊', order: 520 },
    { slug: 'plano_contratado', name: 'Plano Contratado', category: 'financeiro', icon: '📄', order: 530 },
    { slug: 'horario_ponta', name: 'Horário de Ponta', category: 'financeiro', icon: '🕒', order: 540 },
    // Administração. `api_access` veio de "gestao_eletrica": chave de
    // integração não tem relação com energia, é controle de acesso.
    { slug: 'users', name: 'Usuários', category: 'admin', icon: '👥', order: 610 },
    { slug: 'api_access', name: 'Chaves de Integração', category: 'admin', icon: '🛡️', order: 620 },
    { slug: 'settings', name: 'Configurações', category: 'admin', icon: '⚙️', order: 630 },
    { slug: 'system_logs', name: 'Logs do Sistema', category: 'admin', icon: '📃', order: 640 },
    // Admin-only (verificado no frontend). Não entra no seed de user.modules
    // porque a rota já gateia por role='admin' na tela; fica no catálogo para
    // o Sidebar montar o item automaticamente para admin.
    { slug: 'feature_flags', name: 'Recursos em Teste', category: 'admin', icon: '🚩', order: 650 },
];
export const CATEGORY_LABELS = {
    principal: 'Início',
    // Era "Carregadores" — mesmo nome do item que mora dentro dele. "Pátio"
    // nomeia o lugar, os itens nomeiam as coisas.
    carregadores: 'Pátio',
    operacao: 'Operação',
    // "Gestão Elétrica" descrevia a atividade; "Energia" descreve o assunto,
    // que é como alguém procura.
    gestao_eletrica: 'Energia',
    frota: 'Frota',
    financeiro: 'Financeiro',
    admin: 'Administração',
};
/**
 * Rótulo curto da categoria, para o trilho vertical de ícones do menu —
 * onde há ~60px de largura útil. `CATEGORY_LABELS` continua sendo o rótulo
 * longo, usado no cabeçalho do painel que abre ao lado do trilho.
 *
 * Mora aqui e não num mapa local do Sidebar porque `ModuleCategory` é o
 * tipo que garante exaustividade: categoria nova quebra o build, em vez de
 * aparecer sem rótulo em produção.
 */
export const CATEGORY_SHORT = {
    principal: 'Início',
    carregadores: 'Pátio',
    operacao: 'Oper.',
    gestao_eletrica: 'Energ.',
    frota: 'Frota',
    financeiro: 'Fin.',
    admin: 'Admin',
};
// ============================================================
// Contrato de Relatórios de consumo e desempenho da frota.
// Endpoint principal: GET /api/fleet-performance
// Endpoint drill-down: GET /api/fleet-performance/rankings
// ============================================================
/** Indicador canônico. Frontend usa esta constante como discriminador. */
export const RELATORIOS_INDICADOR_KWH_KM = 'kwh_por_km';
/** Segundos de silêncio da fonte a partir dos quais a leitura é STALE. */
export const SUBSTATION_STALE_AFTER_SECONDS = 300;
/** Intervalo de refetch da tela. A UI descreve isso como "auto-atualização". */
export const SUBSTATION_REFRESH_INTERVAL_SECONDS = 30;
/** Horas de `series` por ponto: número de contrato, não detalhe do gerador. */
export const SUBSTATION_SERIES_HOURS = 24;
/** Linhas da tabela de histórico. Também é contrato: a UI dimensiona por ele. */
export const SUBSTATION_HISTORY_ROWS = 15;
/**
 * Arredondamento de SAÍDA do contrato da subestação. Vive aqui porque servidor
 * (simulação e ramo real) e qualquer consumidor têm de arredondar igual: duas
 * implementações do mesmo `round` são duas respostas diferentes para o mesmo
 * número.
 */
export function roundTo(value, decimals) {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}
const MEGA_UNIT = {
    kW: 'MW',
    kVAr: 'MVAr',
    kVA: 'MVA',
    kWh: 'MWh',
};
/**
 * Escala automática de unidade: acima de 1000 divide por 1000 e sobe o prefixo
 * (kW->MW, kVAr->MVAr, kVA->MVA, kWh->MWh).
 *
 * É FUNÇÃO ÚNICA, compartilhada por KPI, cards e eixo do gráfico, justamente
 * para que eixo e KPI nunca discordem de unidade na mesma tela. Não arredonda:
 * quem exibe formata em pt-BR.
 *
 * `ref` — A UNIDADE NÃO É PROPRIEDADE DO VALOR, É DO CONTEXTO DE COMPARAÇÃO.
 *
 * Decidir a dobra kW/MW valor a valor produz irmãos em unidades diferentes na
 * mesma varredura visual. Medido com quatro transformadores de placa idêntica:
 *
 *     980,0 kW | 863,6 kW | 925,4 kW | 1,0 MW
 *
 * O quarto tem 1.047 kW e PARECE o menor da linha. Na coluna de potência da
 * tabela, alinhada à direita em fonte monoespaçada, fica impossível ordenar
 * com o olho. Passando o MESMO `ref` (o maior valor do conjunto exibido) para
 * todos os irmãos, o conjunto inteiro dobra de uma vez e volta a ser
 * comparável.
 *
 * O default `ref = value` mantém a assinatura retrocompatível: quem chama com
 * dois argumentos — o servidor, hoje — obtém exatamente o comportamento
 * anterior, valor a valor.
 *
 * Subir o limiar de 1000 para 10.000 foi considerado e descartado: afastaria a
 * dobra da faixa desta instalação sem eliminar a classe do defeito, e esta é
 * uma tela de vitrine, que vai rodar com outros números.
 */
export function autoScale(value, unit, ref = value) {
    if (!Number.isFinite(value))
        return { value, unit };
    // `ref` não finito (conjunto vazio, NaN) degrada para o próprio valor, que é
    // o comportamento de antes — nunca para uma exceção.
    const escala = Number.isFinite(ref) ? ref : value;
    if (Math.abs(escala) > 1000)
        return { value: value / 1000, unit: MEGA_UNIT[unit] };
    return { value, unit };
}
